import classnames from 'classnames';
import React from 'react';
import {Row} from 'react-table';

import cs from './PropertyOverview.module.scss';

/**
 * A selectable, keyboard-navigable row in one of the portfolio's virtualized
 * tables. Expects `row` to have been through `table.prepareRow`.
 */
export function VirtualizedTableRow<D extends object>({
  row,
  offset,
  gridTemplateColumns,
  onOpen,
  isSelectable,
}: {
  row: Row<D>;
  /** The row's top within the table body, from the virtualizer. */
  offset: number;
  gridTemplateColumns: string;
  onOpen?: (item: D) => void;
  /** Rows this returns false for can't be selected, matching their disabled checkbox. */
  isSelectable?: (item: D) => boolean;
}) {
  const elRef = React.useRef<HTMLTableRowElement>(null);

  const onKeyDown = (ev: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (ev.key === 'Enter') {
      onOpen?.(row.original);
    } else if (ev.key === ' ') {
      if (isSelectable?.(row.original) ?? true) {
        row.toggleRowSelected();
      }
      ev.preventDefault();
    } else if (ev.key === 'ArrowDown') {
      if (elRef.current?.nextElementSibling) {
        (elRef.current.nextElementSibling as HTMLElement).focus();
        ev.preventDefault();
      }
    } else if (ev.key === 'ArrowUp') {
      if (elRef.current?.previousElementSibling) {
        (elRef.current.previousElementSibling as HTMLElement).focus();
        ev.preventDefault();
      }
    }
  };

  const {key, ...rowProps} = row.getRowProps();

  return (
    <tr
      key={key}
      {...rowProps}
      ref={elRef}
      data-id={row.id}
      // cs.row is what SearchBoxFilter focuses when the down arrow is pressed.
      className={classnames(cs.row, {[cs.selected]: row.isSelected})}
      tabIndex={0}
      onKeyDown={onKeyDown}
      style={{
        position: 'absolute',
        transform: `translateY(${offset}px)`,
        width: '100%',
        gridTemplateColumns,
      }}
    >
      {row.cells.map((cell) => {
        const {key: cellKey, ...cellProps} = cell.getCellProps();
        return (
          <td key={cellKey} {...cellProps} className={cell.column.className}>
            {cell.render('Cell')}
          </td>
        );
      })}
    </tr>
  );
}
